/**
 * Which live channels THIS caller subscribes to.
 *
 * Every live surface needs the same two steps: read the caller's tenancy out of
 * the session, then ask `@authe/streaming` for the audiences that tenancy can
 * hear a stream on (platform-wide, tenant-wide, their own user channel, their
 * teams'). Six surfaces had written both out — the same three session fields,
 * the same `.map(subscribeDescriptor)`, and the same three-field dependency
 * array — which is six chances to name the tenancy wrong on a subscription,
 * the one mistake here that reads someone else's rows.
 *
 * `useCallerChannels(STREAM_X)` is that recipe, once:
 *
 *   const channels = useCallerChannels(STREAM_TRANSACTION_GROUPS);
 *   const { rows } = useTransactionStream({ channel: channels, enabled });
 *
 * A surface that builds a wider list (the transactions feed also listens on the
 * platform channel for system pipelines) takes `callerScope(session)` and keeps
 * its own memo.
 */
import * as React from "react";
import {
  streamChannelIdsForCaller,
  subscribeDescriptor,
  type CallerChannelOptions,
  type ChannelDescriptor,
  type ChannelScope,
} from "@authe/streaming";
import { useTeamIds } from "./useTeamRoster";
import { useSession } from "@authe/ui-core/session";

/** The session's tenancy, in the shape the channel ids are derived from. */
export function callerScope(session: ReturnType<typeof useSession>["session"]): ChannelScope {
  return {
    customerId: session?.tenant.customerId,
    orgId: session?.tenant.orgId,
    tenantId: session?.tenant.tenantId,
    userId: session?.user.id,
  };
}

export interface CallerChannelsOptions extends CallerChannelOptions {
  /** Override the live membership read when a caller already has team ids. */
  teamIds?: ReadonlyArray<string>;
}

/**
 * Subscribe descriptors for every audience the caller can hear `stream` on.
 *
 * Memoised on the tenancy fields themselves rather than on the session object,
 * which is a new identity on every refresh — the descriptors must be stable or
 * the subscription tears down and reopens under the surface using it.
 */
export function useCallerChannels(
  stream: string,
  { teamIds, ...options }: CallerChannelsOptions = {},
): ChannelDescriptor[] {
  const { session } = useSession();
  const customerId = session?.tenant.customerId;
  const memberships = useTeamIds();
  const orgId = session?.tenant.orgId;
  const tenantId = session?.tenant.tenantId;
  const userId = session?.user.id;
  const teamKey = JSON.stringify([...new Set(teamIds ?? memberships)].sort());
  const includeGlobal = options.includeGlobal;
  return React.useMemo(
    () =>
      streamChannelIdsForCaller(
        { customerId, orgId, tenantId, userId },
        stream,
        JSON.parse(teamKey) as string[],
        includeGlobal === undefined ? {} : { includeGlobal },
      ).map((id) => subscribeDescriptor(id)),
    [customerId, orgId, tenantId, userId, stream, teamKey, includeGlobal],
  );
}
